import { EpiFormsSubmissionResult } from '@_types/epi-forms';
import Button from '@atoms/buttons/Button';
import { camelToSnakeCaseForObj, getClassFunction, replaceFallbackImageSrc } from '@helpers/functions';
import getComponentMarkup from '@helpers/getComponentMarkup';
import TagCommander from '@helpers/TagCommanders.class';
import { initPhoneReveal } from '@helpers/phoneReveal';
import ContactFormPerson from '@molecules/person/ContactFormPerson';
import { PersonModel } from '@_types/types';
import { applyStoredUtmFieldsToForm } from '@helpers/utm-attribution.service';
import {
  clearValidationStateClasses,
  getSlideFormStepValidationSummaryMessages,
  hideValidationErrors,
  isEpiFormsAsyncStepValidationFailed,
  isEpiFormsClientStepValidationFailed,
  revealFailedValidationErrors,
} from '@helpers/epiFormValidationUi';
import ErrorSummary from '@molecules/form/error-summary/ErrorSummary';

import { setStatus } from './form-submisssion';

const getClass = getClassFunction('kf-m-slide-form');

const elements = document.querySelectorAll(getClass());

const triggerElements = document.querySelectorAll('[data-open-form-id]');

const tcEvent = window.tC?.event;

const handleEventTracking = (form: HTMLElement, eventTrackingData: { [key: string]: string | undefined }, event: Event) => {
  if (eventTrackingData) {
    const { agentName, agentTitle, agentOfficeName } = eventTrackingData;

    form.dataset.agent = JSON.stringify({ agentName, agentTitle, agentOfficeName });

    if (tcEvent && eventTrackingData.triggerLabel) {
      const triggerLabel = eventTrackingData.triggerLabel;

      if (triggerLabel && typeof tcEvent[triggerLabel] === 'function') {
        tcEvent[triggerLabel](event, { ...camelToSnakeCaseForObj(eventTrackingData), form_status: 'form.open' });
      }
    }
  }
};

const resetElements = (form: HTMLElement) => {
  const contactPersonEl = form.querySelector('.kf-m-slide-form__contact-person');

  contactPersonEl?.remove();

  const currentErrorSummary = form?.querySelector('.kf-m-error-summary');

  currentErrorSummary?.remove();
};

const parseJson = <T>(value?: string): T | undefined => {
  if (!value) return undefined;

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const applyPersonInfoAreaValue = (form: HTMLElement, value: string) => {
  const personalInfoAreaEls = form.querySelectorAll<HTMLInputElement>('.PersonInfoArea');

  personalInfoAreaEls.forEach((el) => {
    el.value = value;
    el.setAttribute('value', value);
  });
};

const handlePersonalInfo = (form: HTMLElement, formDataJson?: PersonModel) => {
  const personalInfoAreaEl = form.querySelector<HTMLInputElement>('.PersonInfoArea');
  const defaultValue = personalInfoAreaEl?.dataset.defaultEmail;
  const nextValue = formDataJson?.employeeNumber ?? defaultValue ?? '';

  if (formDataJson?.employeeNumber) {
    form.dataset.openerEmployeeNumber = formDataJson.employeeNumber;
  } else {
    delete form.dataset.openerEmployeeNumber;
  }

  applyPersonInfoAreaValue(form, nextValue);
};

const handleReportEditionId = (form: HTMLElement, reportEditionId: string) => {
  if (!reportEditionId) return;

  form.dataset.openerEmployeeNumber = reportEditionId;
  applyPersonInfoAreaValue(form, reportEditionId);
};

const handleContactPerson = (form: HTMLElement, formDataJson?: PersonModel) => {
  if (formDataJson?.name || formDataJson?.image) {
    const formTitle = form.querySelector('.Form__Title');
    const div = document.createElement('div');

    div.classList.add('kf-m-slide-form__contact-person');
    const child = getComponentMarkup(ContactFormPerson, formDataJson as PersonModel);

    div.innerHTML = child;
    formTitle?.after(div);
  }
};

const handleOpenForm = (
  formId?: string,
  options?: { formData?: string; formEventData?: string; event?: Event; reportEditionId?: string; stepValidationErrorSummary?: boolean },
) => {
  if (!formId) return;
  const form = document.querySelector<HTMLElement>(`[data-form-id="${formId}"]`);
  const formEl = form?.querySelector('form');
  const formEventData = parseJson<{ [key: string]: string | undefined }>(formEl?.dataset.formEvent);
  const formDataJson = parseJson<PersonModel>(options?.formData);
  const eventTrackingData = parseJson<{ [key: string]: string | undefined }>(options?.formEventData) ?? formEventData;

  if (form) {
    const setFormDataset = () => {
      form.dataset.status = 'open';
      if (options?.reportEditionId) {
        form.dataset.useSuccessMessageFromResponse = 'true';
      }
    };

    setFormDataset();

    resetElements(form);

    if (options?.stepValidationErrorSummary) {
      form.dataset.stepValidationErrorSummary = 'true';
    } else {
      form.removeAttribute('data-step-validation-error-summary');
    }

    handleContactPerson(form, formDataJson);

    if (formDataJson?.image) {
      const formImages = form.querySelectorAll<HTMLImageElement>('.kf-a-picture img[data-fallback-image-url]');

      replaceFallbackImageSrc(formImages);
    }

    if (formDataJson) {
      TagCommander.getInstance().run();
      initPhoneReveal(form);
    }

    const handlePrefilledData = () => {
      if (!options?.reportEditionId) {
        handlePersonalInfo(form, formDataJson);
      } else {
        handleReportEditionId(form, options.reportEditionId);
      }
    };

    handlePrefilledData();

    if (options?.event) {
      handleEventTracking(form, eventTrackingData, options.event);
    }

    form.classList.add('kf-m-slide-form--open');

    document.body.style.overflow = 'hidden';
  }
};

const clearClasses = (element: HTMLElement) => {
  const cl = element.classList;

  for (let i = cl.length; i > 0; i--) {
    cl.remove(cl[0]);
  }
};

const resetForm = (form: HTMLFormElement) => {
  if (form) {
    const formStatusMessage = form.querySelector<HTMLElement>('.Form__Status__Message');
    const formSubmissionMessage = form.querySelector('.Form__SubmissionSuccess__Message');
    const formMainBody = form.querySelector('.Form__MainBody');

    form.reset();
    clearValidationStateClasses(form);
    hideValidationErrors(form);

    if (formStatusMessage) {
      clearClasses(formStatusMessage);
      formStatusMessage.classList.add('Form__Status__Message', 'hide');
      formSubmissionMessage?.classList.add('Form__SubmissionSuccess__Message', 'hide');
    }

    if (formMainBody) {
      formMainBody.removeAttribute('style');
    }

    form.querySelector('.kf-m-error-summary')?.remove();
  }
};

const handleCloseForm = (formId?: string, options?: { event: Event }) => {
  if (!formId) return;

  const formWrapper = document.querySelector<HTMLElement>(`[data-form-id="${formId}"]`);
  const formEl = formWrapper?.querySelector('form');

  if (formWrapper && formEl) {
    formWrapper.dataset.status = 'closed';
    formWrapper.removeAttribute('data-use-success-message-from-response');
    formWrapper.classList.remove('kf-m-slide-form--open');
    document.body.style.overflow = '';

    const dataFormEvent = formEl.dataset.formEvent;
    const dataAgent = formWrapper.dataset.agent;
    let eventTrackingData = dataFormEvent ? JSON.parse(dataFormEvent) : undefined;

    if (dataAgent) eventTrackingData = { ...eventTrackingData, ...JSON.parse(dataAgent) };

    if (tcEvent && eventTrackingData?.triggerLabel) {
      if (tcEvent && eventTrackingData.triggerLabel) {
        tcEvent[eventTrackingData.triggerLabel](options?.event ?? new Event('closeForm'), {
          ...camelToSnakeCaseForObj(eventTrackingData),
          form_status: 'form.close',
        });
      }
    }

    formWrapper.removeAttribute('data-step-validation-error-summary');
    resetForm(formEl);
  }
};

const addTryAgainButton = (form: HTMLElement) => {
  const errorWrapper = form.querySelector('.kf-m-form__form-reponse-status');

  if (errorWrapper) {
    const tryAgainBtn = form.querySelector<HTMLElement>('.kf-m-form__try-again');

    if (tryAgainBtn) return;

    const btn = getComponentMarkup(Button, { text: 'Try again', styleModifier: ['primary', 'full-width'], className: 'kf-m-form__try-again' });
    const div = document.createElement('div');

    div.innerHTML = btn;
    const btnEl = div.firstChild as HTMLElement;

    btnEl.addEventListener('click', () => {
      const formEl = form.querySelector('form');

      if (formEl) {
        resetForm(formEl);
        const formWrapper = form.closest<HTMLElement>('.kf-m-slide-form');

        if (formWrapper) {
          formWrapper.dataset.status = 'open';
        }
      }
    });

    errorWrapper.appendChild(btnEl);
  }
};

const handleForm = (form: HTMLElement) => {
  const closeBtn = form.querySelector(getClass('header__close'));

  closeBtn?.addEventListener('click', (e) => {
    handleCloseForm(form.dataset.formId, { event: e });
  });

  const formOverlay = form.querySelector(getClass('overlay'));

  formOverlay?.addEventListener('click', (e) => {
    handleCloseForm(form.dataset.formId, { event: e });
  });

  const footerCloseBtns = form.querySelectorAll(getClass('footer__close'));

  [].forEach.call(footerCloseBtns, (footerCloseBtn: HTMLElement) => {
    const text = footerCloseBtn.textContent;
    const span = document.createElement('span');

    span.textContent = text;
    footerCloseBtn.innerHTML = '';
    footerCloseBtn.appendChild(span);
    footerCloseBtn?.addEventListener('click', (e) => {
      handleCloseForm(form.dataset.formId, { event: e });
    });
  });

  addTryAgainButton(form);
};

const handleOpenButtonClick = (element: HTMLElement) => {
  const onClick = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    const formId = element.dataset.openFormId;
    // Prefer getAttribute so HTML-encoded JSON in data-form-data is read reliably.
    const formData = element.getAttribute('data-form-data') ?? element.dataset.formData;
    const formEventData = element.getAttribute('data-form-event') ?? element.dataset.formEvent;
    const reportEditionId = element.dataset.reportEditionId;
    const stepValidationErrorSummary = element.getAttribute('data-step-validation-error-summary') === 'true';

    handleOpenForm(formId, { formData, formEventData, event: e, reportEditionId, stepValidationErrorSummary });
  };

  element.addEventListener('click', onClick);
  element.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      onClick(event);
    }
  });
};

[].forEach.call(elements, handleForm);
[].forEach.call(triggerElements, handleOpenButtonClick);

window.addEventListener('openForm', (event: Event) => {
  const customEvent = event as CustomEvent;

  handleOpenForm(customEvent.detail.formId);
});

window.addEventListener('closeForm', (event: Event) => {
  const customEvent = event as CustomEvent;

  handleCloseForm(customEvent.detail.formId);
});

// epi form script
if (typeof window.$$epiforms !== 'undefined') {
  const handleFormSubmitted = (e: EpiFormsSubmissionResult) => {
    const form = e.target as HTMLFormElement;
    const formWrapper = form.closest<HTMLElement>('.kf-m-slide-form');
    const useSuccessMessageFromResponse = formWrapper?.dataset.useSuccessMessageFromResponse;

    const handleSuccessMessageFromResponse = () => {
      if (e.isSuccess && useSuccessMessageFromResponse === 'true' && e.returnedResult?.message) {
        const successMessageElement = form.querySelector<HTMLElement>('.Form__SubmissionSuccess__Message');

        if (successMessageElement) {
          successMessageElement.innerHTML = e.returnedResult.message;
        }
      }
    };

    handleSuccessMessageFromResponse();

    if (!formWrapper) return;
    const dataFormEvent = form.dataset.formEvent;
    const dataAgent = formWrapper.dataset.agent;
    let eventTrackingData = dataFormEvent ? JSON.parse(dataFormEvent) : undefined;

    if (dataAgent) eventTrackingData = { ...eventTrackingData, ...JSON.parse(dataAgent) };

    if (tcEvent && eventTrackingData?.triggerLabel) {
      if (tcEvent && eventTrackingData.triggerLabel) {
        tcEvent[eventTrackingData.triggerLabel](new Event('formSubmit'), {
          ...camelToSnakeCaseForObj(eventTrackingData),
          form_status: 'form.submit.success',
          lead_id: e.returnedResult?.additionalParams?.eFormId,
        });
      }
    }

    if (formWrapper) {
      setStatus(form, formWrapper, e as EpiFormsSubmissionResult);
    }
  };

  const handleErrors = (e: EpiFormsSubmissionResult) => {
    const form = e.target as HTMLFormElement;
    const formWrapper = form.closest<HTMLElement>('.kf-m-slide-form');

    if (formWrapper) {
      setStatus(form, formWrapper, e as EpiFormsSubmissionResult);
    }
  };

  const handleFormsStartSubmitting = (event: Event) => {
    const form = event.target as HTMLFormElement;
    const formWrapper = form.closest<HTMLElement>('.kf-m-slide-form');
    const mainArea = formWrapper?.querySelector('.kf-m-slide-form__main');
    const openerEmployeeNumber = formWrapper?.dataset.openerEmployeeNumber;

    // Re-apply opener GUID in case the hidden field was reset before submit.
    if (formWrapper && openerEmployeeNumber) {
      applyPersonInfoAreaValue(formWrapper, openerEmployeeNumber);
    }

    applyStoredUtmFieldsToForm(form);

    if (!formWrapper || !mainArea) {
      return;
    }

    mainArea.scrollTo(0, 0);
    formWrapper.classList.add('no-scroll');
  };

  const epiFormsSelector = getClass('form .EPiServerForms');

  if (typeof window.$$epiforms === 'function') {
    const epiFormsInstance = window.$$epiforms(epiFormsSelector);

    if (epiFormsInstance && typeof epiFormsInstance.on === 'function') {
      epiFormsInstance.on('formsSubmitted', handleFormSubmitted);
      epiFormsInstance.on('formsSubmittedError', handleErrors);
      epiFormsInstance.on('formsStartSubmitting', handleFormsStartSubmitting);
      epiFormsInstance.on('formsStepValidating', (event: EpiFormsSubmissionResult) => {
        const form = event.target as HTMLFormElement;

        if (isEpiFormsClientStepValidationFailed(event) || isEpiFormsAsyncStepValidationFailed(event)) {
          revealFailedValidationErrors(form);
        }

        const errorMessages = getSlideFormStepValidationSummaryMessages(event, form);

        const currentErrorSummary = form.querySelector('.kf-m-error-summary');

        currentErrorSummary?.remove();

        if (errorMessages.length === 0) {
          return;
        }

        const formMainBody = form.querySelector<HTMLElement>('.Form__MainBody');
        const errorSummaryEl = getComponentMarkup(ErrorSummary, { title: 'There is a problem', items: errorMessages });
        const errorSummary = document.createElement('div');

        errorSummary.innerHTML = errorSummaryEl;
        formMainBody?.before(errorSummary.firstChild as Node);
      });
    }
  }
}

document.querySelectorAll<HTMLElement>('.kf-m-slide-form__main').forEach((element) => {
  element.addEventListener('transitionend', (event: TransitionEvent) => {
    const parent = element.closest<HTMLElement>('.kf-m-slide-form');
    const propertyName = event.propertyName;

    if (propertyName !== 'right') {
      return;
    }

    if (parent?.classList.contains('kf-m-slide-form--open')) {
      parent.classList.add('kf-m-slide-form--open-animation-end');
    } else {
      parent?.classList.remove('kf-m-slide-form--open-animation-end');
    }
  });
});
